import { create } from 'zustand'
import axios from 'axios'
import { BACKEND_URL, mediaUrl } from '../services/config'
import { isAccentReadable, isValidHexColor } from '../utils/brandContrast'

// White Label Phase 1 — web half (spec
// docs/superpowers/specs/2026-07-27-viewsx-white-label-design.md §4.1).
//
// The brand is resolved by the server from the request Host header and served
// UNAUTHENTICATED at GET /api/brand, because the very first surface that needs
// it — the login page — has no JWT to present.
//
// Three hard rules this store exists to enforce:
//   1. First paint NEVER waits on the network. The initial state below IS the
//      stock brand, so the app renders correctly before (and without) the
//      fetch. `fetchBrand()` only ever replaces it.
//   2. A failed fetch is a no-op, not an error. An unreachable /api/brand must
//      not white-screen the app.
//   3. An arbitrary customer accent is contrast-validated before it touches
//      the theme — see utils/brandContrast.ts.
//
// NOT in this increment: the serve-time HTML injection (spec §6). The inline
// boot splash in index.html stays unbranded on purpose — resolving it client
// side would reintroduce the exact flash the inline splash exists to prevent.

export interface Brand {
  /** Full product name — document title + login wordmark. */
  name: string
  /** Compact name — rail tooltip, mobile top-bar fallback, initials badge. */
  shortName: string
  /** `#rrggbb`, or null to leave the compiled-in theme accent alone. */
  accentHex: string | null
  logoLightUrl: string | null
  logoDarkUrl: string | null
  faviconUrl: string | null
  supportEmail: string | null
  /**
   * True when the visitor is on an unbranded domain and no Brand row matched.
   * Stock branding wins in that case — see `resolveAccent`.
   */
  isDefault: boolean
}

/**
 * Stock branding, compiled in.
 *
 * These values MUST mirror what ships statically today, so that a failed or
 * absent /api/brand leaves the product looking exactly as it does now:
 *   - `name`      → `<title>Business OS</title>` in index.html
 *   - `shortName` → the rail mark's title/alt and MobileTopBar's fallback
 *   - `accentHex` → null, i.e. `--color-primary` in src/css/theme.css stands
 */
export const DEFAULT_BRAND: Brand = {
  name: 'Business OS',
  // Stock short name, deliberately NOT 'Rx2Go'. The rail used to hardcode a
  // customer's name; that is exactly what white label exists to remove, and it
  // would otherwise be printed on the vendor's own domain and on every tenant
  // without a brand row. Kept byte-identical to the server's DEFAULT_BRAND so
  // an unbranded host does not visibly flip when /api/brand resolves.
  shortName: 'Business OS',
  accentHex: null,
  logoLightUrl: null,
  logoDarkUrl: null,
  faviconUrl: null,
  supportEmail: null,
  isDefault: true,
}

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v.trim() : null

/**
 * Coerce whatever /api/brand returned into a complete Brand. Every field is
 * optional on the wire and any of them may be null; a partial or malformed
 * payload degrades field-by-field to the stock value rather than poisoning
 * the whole brand.
 */
export function normalizeBrand(raw: unknown): Brand {
  const r = (raw ?? {}) as Record<string, unknown>
  return {
    name: str(r.name) ?? DEFAULT_BRAND.name,
    shortName: str(r.shortName) ?? str(r.name) ?? DEFAULT_BRAND.shortName,
    accentHex: str(r.accentHex),
    logoLightUrl: str(r.logoLightUrl),
    logoDarkUrl: str(r.logoDarkUrl),
    faviconUrl: str(r.faviconUrl),
    supportEmail: str(r.supportEmail),
    isDefault: r.isDefault !== false,
  }
}

/**
 * The accent to actually paint, or null to leave the theme untouched.
 *
 * Rejected, in order:
 *   - no accent supplied
 *   - `isDefault` — the visitor is on an unbranded domain, so the stock accent
 *     in theme.css is the source of truth. Painting the API's placeholder
 *     default over it would repaint every existing unbranded deployment.
 *   - unparseable hex (also stops anything injectable reaching setProperty)
 *   - fails the contrast floor against `--color-primary-foreground`
 */
export function resolveAccent(brand: Brand): string | null {
  const accent = brand.accentHex
  if (!accent) return null
  if (brand.isDefault) return null
  if (!isValidHexColor(accent)) return null
  if (!isAccentReadable(accent)) return null
  return accent
}

/** Swap the document's `<link rel="icon">` href. No-op without a favicon. */
function applyFavicon(url: string): void {
  const links = document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]')
  if (links.length === 0) {
    const link = document.createElement('link')
    link.rel = 'icon'
    link.href = url
    document.head.appendChild(link)
    return
  }
  links.forEach((link) => {
    link.href = url
    // The stock icon is declared `type="image/svg+xml"`; a per-brand upload is
    // usually a PNG/ICO. A wrong explicit type makes some browsers skip it.
    link.removeAttribute('type')
  })
}

/**
 * Push the brand onto the document: title, favicon, accent token.
 *
 * Accent goes on the root element's INLINE style, which beats both `@theme`
 * (:root) and the `.dark` class override, so one write covers both themes and
 * survives a theme toggle. Never hardcode a colour anywhere else — this is the
 * single place the token system is fed from.
 */
export function applyBrandToDocument(brand: Brand): void {
  if (typeof document === 'undefined') return

  document.title = brand.name

  const favicon = brand.faviconUrl ? mediaUrl(brand.faviconUrl) : null
  if (favicon) applyFavicon(favicon)

  const accent = resolveAccent(brand)
  const root = document.documentElement
  if (accent) {
    root.style.setProperty('--color-primary', accent)
    root.style.setProperty('--color-ring', accent)
  } else {
    // Idempotent: re-applying a brand that lost its accent must hand the
    // token back to theme.css rather than leave the previous value stuck.
    root.style.removeProperty('--color-primary')
    root.style.removeProperty('--color-ring')
  }
}

interface BrandState {
  brand: Brand
  /** True once a /api/brand response has been applied (success or default). */
  loaded: boolean
  /** Set + apply a brand directly. Used by the fetch and by tests. */
  setBrand: (raw: unknown) => void
  /** Fetch /api/brand once. Never throws, never blocks first paint. */
  fetchBrand: () => Promise<void>
}

let inFlight: Promise<void> | null = null

export const useBrandStore = create<BrandState>((set, get) => ({
  brand: DEFAULT_BRAND,
  loaded: false,

  setBrand: (raw: unknown) => {
    const brand = normalizeBrand(raw)
    set({ brand, loaded: true })
    applyBrandToDocument(brand)
  },

  fetchBrand: async () => {
    if (get().loaded) return
    if (inFlight) return inFlight
    inFlight = (async () => {
      try {
        // Bare axios, not services/api: /api/brand is public, and the shared
        // client's interceptors (auth header, 401 refresh, X-Server-Id) have
        // no business running before the user is even logged in.
        const res = await axios.get(`${BACKEND_URL}/api/brand`)
        get().setBrand(res.data)
      } catch {
        // Unreachable / 404 / malformed — keep the stock brand. Deliberately
        // silent: the branding layer is cosmetic and must never be able to
        // take the app down. `loaded` stays false so a later caller may retry.
      } finally {
        inFlight = null
      }
    })()
    return inFlight
  },
}))

/** Test seam — reset the module-level single-flight guard between cases. */
export function __resetBrandStore(): void {
  inFlight = null
  useBrandStore.setState({ brand: DEFAULT_BRAND, loaded: false })
  if (typeof document !== 'undefined') {
    document.documentElement.style.removeProperty('--color-primary')
    document.documentElement.style.removeProperty('--color-ring')
  }
}
